/**
 * @fileoverview Shared fire hazard zone types used across mobile-pwa, tenant-web,
 *               system-web, and backend services.
 * @module core-domain/fireHazard/types
 *
 * Responsibilities:
 * - Define the canonical fire hazard severity levels.
 * - Define the FireHazardZone interface for zone data.
 * - Define the ParcelHazardStamp interface for per-parcel hazard data.
 *
 * Dependencies:
 * - None (pure type definitions).
 *
 * Architecture:
 * - These types are shared across all apps to ensure consistency.
 * - The hazard levels follow USFS/CAL FIRE conventions.
 * - ParcelHazardStamp is stored on entity documents after hazard stamping.
 *
 * Notes:
 * - Hazard data comes from authoritative sources (USFS, CAL FIRE, NIFC).
 * - The WUI (Wildland-Urban Interface) flag indicates parcels at higher risk.
 */

/**
 * @contract FireHazardLevel
 * Purpose: Severity classification for fire hazard zones.
 * Fields:  'low' | 'medium' | 'high' | 'extreme'
 * Rules:   Follows USFS/CAL FIRE severity conventions.
 * Sync:    Static type — not persisted directly.
 */
export type FireHazardLevel = 'low' | 'medium' | 'high' | 'extreme';

/**
 * @contract FireHazardSource
 * Purpose: Identifies the authoritative data source for hazard zones.
 * Fields:  'USFS' | 'CAL_FIRE' | 'NIFC' | 'STATE' | 'LOCAL'
 * Rules:   Used for attribution and data provenance.
 * Sync:    Static type — not persisted directly.
 */
export type FireHazardSource = 'USFS' | 'CAL_FIRE' | 'NIFC' | 'STATE' | 'LOCAL';

/**
 * @contract FireHazardZone
 * Purpose: Represents a fire hazard zone polygon from an authoritative source.
 * Fields:
 *   - id: unique identifier for the zone
 *   - hazardLevel: severity classification
 *   - source: data source identifier
 *   - zoneName: human-readable zone name
 *   - zoneCode: source-specific zone code
 *   - inWUI: whether the zone is in the Wildland-Urban Interface
 *   - geometry: GeoJSON geometry (Polygon or MultiPolygon)
 *   - updatedAt: when the zone data was last updated
 * Rules:
 *   - hazardLevel drives map styling (fill color, opacity).
 *   - inWUI zones may have additional inspection requirements.
 * Sync:
 *   - Zones are loaded from a hosted vector tileset or GeoJSON source.
 *   - Not stored in Dexie — rendered directly on the map.
 */
export interface FireHazardZone {
  id: string;
  hazardLevel: FireHazardLevel;
  source: FireHazardSource;
  zoneName?: string;
  zoneCode?: string;
  inWUI: boolean;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  updatedAt?: Date;
}

/**
 * @contract ParcelHazardStamp
 * Purpose: Fire hazard data stamped onto a parcel/entity after spatial analysis.
 * Fields:
 *   - hazardLevel: the highest hazard level intersecting the parcel
 *   - source: data source for the hazard classification
 *   - inWUI: whether the parcel is in the Wildland-Urban Interface
 *   - zoneId: ID of the primary hazard zone (if available)
 *   - zoneName: name of the primary hazard zone
 *   - stampedAt: when the hazard stamp was applied
 *   - confidence: confidence score (0-1) for the classification
 * Rules:
 *   - When a parcel intersects multiple zones, use the highest hazard level.
 *   - Stamps are refreshed periodically via the fire hazard sync job.
 * Sync:
 *   - Stored on the entity document in the tenant database.
 *   - Synced to Dexie via the standard entity sync mechanism.
 */
export interface ParcelHazardStamp {
  hazardLevel: FireHazardLevel;
  source: FireHazardSource;
  inWUI: boolean;
  zoneId?: string;
  zoneName?: string;
  stampedAt: Date;
  confidence?: number;
}

/**
 * Hazard level priority for determining the "worst" hazard when multiple zones
 * intersect a parcel. Higher number = higher priority (more severe).
 */
export const HAZARD_LEVEL_PRIORITY: Record<FireHazardLevel, number> = {
  low: 1,
  medium: 2,
  high: 3,
  extreme: 4,
};

/**
 * Get the more severe of two hazard levels.
 */
export function getHigherHazardLevel(
  a: FireHazardLevel,
  b: FireHazardLevel,
): FireHazardLevel {
  return HAZARD_LEVEL_PRIORITY[a] >= HAZARD_LEVEL_PRIORITY[b] ? a : b;
}

/**
 * @contract FireHazardColors
 * Purpose: Standard colors for fire hazard visualization.
 * Fields:  fill colors and outline colors for each hazard level.
 * Rules:   Follows USFS/CAL FIRE color conventions.
 * Sync:    Static constants — not persisted.
 */
export const FIRE_HAZARD_FILL_COLORS: Record<FireHazardLevel, string> = {
  low: '#22c55e',      // green
  medium: '#eab308',   // yellow
  high: '#f97316',     // orange
  extreme: '#dc2626',  // red
};

export const FIRE_HAZARD_OUTLINE_COLORS: Record<FireHazardLevel, string> = {
  low: '#15803d',
  medium: '#a16207',
  high: '#c2410c',
  extreme: '#991b1b',
};

/**
 * Human-readable labels for hazard levels.
 */
export const HAZARD_LEVEL_LABELS: Record<FireHazardLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  extreme: 'Extreme',
};

/**
 * @contract FireHazardOverlayOpacityDefaults
 * Purpose: Centralized opacity defaults/bounds for the fire hazard overlay
 *          layer, shared by every app's fireHazardLayers.ts (mobile-pwa,
 *          tenant-web, system-web) so the "parcel colors must always show
 *          through" rule from the Phase 5.9 implementation plan is enforced
 *          in one place instead of three independently-hardcoded copies.
 * Fields:
 *   - DEFAULT_FILL: default fill-opacity for the hazard fill layer.
 *   - MIN_FILL / MAX_FILL: the Layer Manager opacity slider's allowed
 *     range — capped at 0.5 (not 1.0) so a user can never dial the overlay
 *     up to the point of obscuring parcel inspection-status colors.
 *   - DEFAULT_OUTLINE: default line-opacity for the hazard outline layer.
 * Rules:   Fill opacity must stay within [MIN_FILL, MAX_FILL] — see the
 *          implementation plan's "Opacity is critical" section.
 * Sync:    Static constants — not persisted.
 */
export const FIRE_HAZARD_OVERLAY_OPACITY = {
  DEFAULT_FILL: 0.18,
  MIN_FILL: 0,
  MAX_FILL: 0.5,
  DEFAULT_OUTLINE: 0.6,
} as const;

/**
 * Human-readable attribution labels for each fire hazard data source, for
 * map legends/popups and admin UI source lists.
 */
export const FIRE_HAZARD_SOURCE_LABELS: Record<FireHazardSource, string> = {
  CAL_FIRE: 'CAL FIRE Fire Hazard Severity Zones',
  USFS: 'USFS Wildland-Urban Interface',
  NIFC: 'NIFC Active Fire Perimeters',
  STATE: 'State Wildfire Risk Portal',
  LOCAL: 'Local Fire Hazard Data',
};

/**
 * @contract ACTIVE_FIRE_STYLE
 * Purpose: Styling for the active-fire perimeter layer (Phase 5.9), shared
 *          by every app's fireHazardLayers.ts so mobile-pwa, tenant-web,
 *          and system-web render active fires identically.
 * Fields:
 *   - COLOR: bright red per the implementation plan ("Active Fire
 *     Perimeter | Bright red pulsing (#ef4444)").
 *   - PULSE_MIN_OPACITY / PULSE_MAX_OPACITY: the outline-opacity range the
 *     pulsing animation oscillates between.
 *   - PULSE_PERIOD_MS: one full pulse cycle (fade out + back in), in
 *     milliseconds.
 * Rules:   Active fire perimeters render OUTLINE ONLY, no fill — see the
 *          implementation plan's "does not obscure any parcel data
 *          underneath" requirement.
 * Sync:    Static constants — not persisted.
 */
export const ACTIVE_FIRE_STYLE = {
  COLOR: '#ef4444',
  PULSE_MIN_OPACITY: 0.35,
  PULSE_MAX_OPACITY: 0.95,
  PULSE_PERIOD_MS: 1500,
} as const;
